import { Button, useTheme } from "@mui/material";
import { Portal } from "../../Portals";
import { useState } from "react";
import api from "../../login/interceptors";
import { BASE_URL } from "../../../Constants";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import useFocusTrap from "../../../hooks/useFocusTrap";

export default function JobStatusToggle({ job, refreshData }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const modalRef = useFocusTrap(isModalOpen , ()=> setIsModalOpen(false));

  const openModal = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  const toggleStatus = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    try {
      const newStatus = job.status === "active" ? "archived" : "active";

      await api.post(`${BASE_URL}/api/job-descriptions/`, {
        jd_uuid: job.jd_uuid,
        status: newStatus
      });

      closeModal(e);
      if (typeof refreshData === "function") {
        refreshData();
      }
    } catch (error) {
      console.error("Error updating job status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={(e) => openModal(e)}
        role="button"
        tabIndex={0}
        className="cursor-pointer inline-flex items-center justify-center gap-2 py-1 hover:!text-accent-blue"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal(e);
          }
        }}
      >
        <span>{job.status === "active" ? "Active" : "Archived"}</span>
        <PencilIcon className="h-3 w-4" />
      </div>

      {isModalOpen && (
        <Portal>
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70"
          >
            <div
              className="bg-white rounded-md w-full max-w-47rem overflow-hidden relative flex flex-col shadow-md max-sm:mx-2 mt-[70px]"
              ref={modalRef}
              style={{ borderColor: theme.palette.secondary.v4 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <div
                className="flex justify-between items-center px-5 py-3 border-b"
                style={{ borderColor: theme.palette.secondary.v4 }}
              >
                <h3
                  id="modal-title"
                  className="font-semibold tracking-wide"
                >
                  Change Job Status
                </h3>
                <button
                  onClick={(e) => closeModal(e)}
                  aria-label="Close modal"
                  className="bg-black text-white rounded-full p-1"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-8">
                <p style={{ color: theme.palette.text.main }}>
                  Are you sure you want to change the status of
                  {job.title && (
                    <span className="px-1 font-bold">
                      "{job.title}"
                    </span>
                  )}
                  from{" "}
                  <span className="font-bold">
                    {job.status === "active" ? "Active" : "Archived"}
                  </span>{" "}
                  to{" "}
                  <span className="font-bold">
                    {job.status === "active" ? "Archived" : "Active"}
                  </span>
                  ?
                </p>
              </div>

              <div
                className="px-5 py-3 border-t flex justify-between bg-white"
                style={{ borderColor: theme.palette.secondary.v4 }}
              >
                <button
                  onClick={(e) => closeModal(e)}
                  className="rounded-md"
                >
                  Cancel
                </button>
                <Button
                  onClick={(e) => toggleStatus(e)}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.info.main,
                    padding: "2px 10px"
                  }}
                >
                  {isLoading
                    ? "Processing..."
                    : job.status === "active"
                      ? "Archive Job"
                      : "Activate Job"}
                </Button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
